
import moment from 'moment';
import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import { State } from 'vuex-class';
import { IUserState } from '../../../src/store/contract';
import { RotationTermPhrases } from '../../shared/configuration/BarometerConfig';
import { IAccountHyperMediaRepresentation } from '../../shared/contracts/AccountContract';
import { Point, RotationTerm } from '../../shared/contracts/AnswerContract';
import { IUserAllocationHypermediaRepresentation } from "../../shared/contracts/UserContract";
import { NewAnswerModel } from '../../views/questionnaires/QuestionnaireModel';

@Component
export default class Rotation extends Vue {
  @Prop() answer: NewAnswerModel;
  @Prop() account: IAccountHyperMediaRepresentation;
  @State('user') userState: IUserState;

  @Watch('answerAccountId')
  onAnswerAccountIdChanged(val: NewAnswerModel) {
    this.getAccountLifetimer();
  }

  @Emit('answerChanged')
  @Watch('answerCopy', {deep: true})
  onAnswerCopyChanged(val: NewAnswerModel) {
  }

  @Watch('quantativeAnswer')
  onQuantativeAnswerChanged(val: Point) {
    this.clearRotationTerm();
  }


  @Watch('account')
  onAccountChanged(val: IAccountHyperMediaRepresentation) {
    this.answerCopy.account = val;
    this.getAccountLifetimer();
  }

  private quantitativeOptions: { text: string; value: Point }[] = [];
  private quantitativeImages: Map<string, string> = new Map();
  private quantitativeText: Map<string, string> = new Map();
  private rotationTermOptions: { text: string; value: RotationTerm }[] = [];

  public answerCopy: NewAnswerModel = null;
  public lifetimer: number[] = [0, 0, 0];

  selectedAccount: null;

  created() {
    this.answerCopy = new NewAnswerModel(this.answer.id, this.answer.answerType, this.answer.quantitative, this.answer.rotationTerm);
    this.quantitativeOptions = [
      {
        text: 'Not quite yet',
        value: Point.Positive,
      },
      {
        text: 'Maybe',
        value: Point.Neutral,
      },
      {
        text: 'Yes',
        value: Point.Negative,
      },
    ];
    this.quantitativeImages.set('empty', 'empty-state2.svg');
    this.quantitativeImages.set('Positive', 'not-yet.svg');
    this.quantitativeImages.set('Neutral', 'maybe.svg');
    this.quantitativeImages.set('Negative', 'lets-go.svg');

    this.quantitativeText.set('empty', 'Still Contemplating');
    this.quantitativeText.set('Positive', 'No, still a happy camper on my current account');
    this.quantitativeText.set('Neutral', 'Maybe, but not just yet ready to start walking');
    this.quantitativeText.set('Negative', 'Yes, ready for a change within...');

    this.rotationTermOptions = [
      {
        text: RotationTermPhrases[RotationTerm.Months_3],
        value: RotationTerm.Months_3,
      },
      {
        text: RotationTermPhrases[RotationTerm.Months_6],
        value: RotationTerm.Months_6,
      },
      {
        text: RotationTermPhrases[RotationTerm.Months_9],
        value: RotationTerm.Months_9,
      },
    ];
  }

  getAnswerVisual(value = 'empty'): string {
    return this.quantitativeImages.get(value);
  }

  getAnswerText(value = 'empty') {
    return this.quantitativeText.get(value);
  }

  public get answerAccountId() {
    return (this.answerCopy && this.answerCopy.account && this.answerCopy.account.accountId) || null;
  }

  public get quantativeAnswer() {
    if (!this.answerCopy) {
      return null;
    }

    return this.answerCopy.quantitative;
  }

  public clearRotationTerm() {
    delete this.answerCopy.rotationTerm;
  }

  public getAccountLifetimer() {
    this.lifetimer = [0, 0, 0];
    let activeAllocations = this.userState.activeAllocations;
    if (activeAllocations.length === 0) {
      return;
    }
    if (!this.answerCopy || !this.answerCopy.account) {
      return;
    }

    // filter the active allocations on those where the account name is the same as the account name of the answer
    let allocationsForAccount = activeAllocations.filter((a) => a.accountName === this.answerCopy.account.name);
    if (allocationsForAccount.length === 0) {
      return;
    }
    // find all the past allocations that are consecutive (meaning they are no breaks between allocations that are longer than 30 days) and get the startdate of the earliest allocation
    let startDateOfAllocation = this.getEarliestStartDateOfPastConsecutiveAllocations(new Date(), allocationsForAccount);

    // calculate the time since the allocation start date and the current date
    let startDate = moment(startDateOfAllocation);
    let today = moment();

    // transform the time since allocation to an array that holds the years, months and days
    let duration = moment.duration(today.diff(startDate));
    this.lifetimer[0] = duration.years();
    this.lifetimer[1] = duration.months();
    this.lifetimer[2] = duration.days();
  }

  public getEarliestStartDateOfPastConsecutiveAllocations(currentDate: Date, allocations: IUserAllocationHypermediaRepresentation[]): Date {
    let pastAllocations = allocations.filter((a) => new Date(a.startDate) <= currentDate);
    if (pastAllocations.length === 0) {
      return currentDate;
    }

    let pastAllocationWithLatestEndDate: IUserAllocationHypermediaRepresentation = pastAllocations[0];
    for (const allocation of pastAllocations) {
      if (allocation.expectedEndDate > pastAllocationWithLatestEndDate.expectedEndDate) {
        pastAllocationWithLatestEndDate = allocation;
      }
    }

    let thirtyDayOffset = (24 * 60 * 60 * 1000) * 30;
    let thirtyDaysBeforeCurrentDate = new Date(new Date().setTime(currentDate.getTime() - thirtyDayOffset));

    if (pastAllocationWithLatestEndDate.expectedEndDate < thirtyDaysBeforeCurrentDate) {
      return currentDate;
    }
    return this.getEarliestStartDateOfPastConsecutiveAllocations(pastAllocationWithLatestEndDate.startDate, allocations.filter((a) => a.allocationId !== pastAllocationWithLatestEndDate.allocationId));
  }

  public getQuantitativeAnswerLabel(answer: Point): string {
    let label = this.quantitativeOptions.find((v) => v.value === answer);
    return label && label.text;
  }

  isCommentRequired(): boolean {
    return this.answerCopy.quantitative === Point.Neutral || this.answerCopy.quantitative === Point.Negative;
  }

  isTermRequired(): boolean {
    return this.answerCopy.quantitative === Point.Negative;
  }
}
